<template>
  <v-card v-if="type.is_active" variant="elevated" elevation="6">
    <v-card-item>
      <v-card-title class="text-body-1 font-weight-bold text-wrap">
        {{ type.producttype_name }}
      </v-card-title>
    </v-card-item>

    <v-card-text>
      <v-row no-gutters>
        <v-col cols="12" xxl="8" xl="8" lg="8" md="8">
          <div class="d-flex">
            <div class="me-2 d-flex" :class="{
              'align-start': type.business_hours.length > 1,
              'align-center': type.business_hours.length == 1,
            }">
              <v-icon class="me-1" :class="{ 'mt-1': type.business_hours.length > 1 }"
                size="medium">mdi-calendar-blank</v-icon>
              <div class="d-flex flex-column">
                <template v-for="(businessHour, index) in type.business_hours" :key="index">
                  <div>
                    {{
                      $t("non-event-card.day", {
                        day: getDayInWeek(businessHour.day_start % 7),
                      })
                    }}
                    -
                    {{
                      $t("non-event-card.day", {
                        day: getDayInWeek(businessHour.day_end % 7),
                      })
                    }}, {{ businessHour.open_hour.slice(0, 5) }} -
                    {{ businessHour.closing_hour.slice(0, 5) }}
                  </div>
                </template>
              </div>
            </div>
          </div>

          <div class="d-flex align-center">
            <v-icon class="me-1 align-self-start pt-1" size="medium">mdi-calendar-multiselect</v-icon>
            <div>
              {{
                $t("non-event-card.expiry", product.product_expiry_range, {
                  n: product.product_expiry_range,
                })
              }}
            </div>
          </div>
        </v-col>

        <v-col class="d-flex align-end justify-end" cols="12" xxl="4" xl="4" lg="4" md="4">
          <!-- handling unlimited qty -->
          <v-chip v-if="type.is_limited &&
            type.producttype_qty -
            type.producttype_booked_qty <=
            type.qty_threshold_sale" color="error" class="text-caption" label>
            {{ $t("non-event-card.out-of-stock") }}
          </v-chip>
          <v-chip v-else-if="type.is_limited && type.producttype_qty -
            type.producttype_booked_qty <=
            type.qty_threshold_ui" color="warning" class="text-caption" label>
            {{ $t("non-event-card.almost-out-of-stock") }}
          </v-chip>
          <v-chip v-else-if="type.is_limited && type.qty_visibility" color="widget-color" class="text-caption" label>
            {{ $t("non-event-card.remaining-tickets") }}:&nbsp;
            <span>{{
              type.producttype_qty - type.producttype_booked_qty >= 0
                ? type.producttype_qty - type.producttype_booked_qty
                : 0
            }}</span>
          </v-chip>
        </v-col>
      </v-row>

      <v-divider class="my-2 border-dashed" color="black" thickness="3"></v-divider>
    </v-card-text>

    <v-card-text class="pt-0">
      <!-- handling stok habis, stok kurang dari max order, tambah stok tidak boleh lebih dari max order -->
      <v-row no-gutters align="center">
        <v-col cols="12" xxl="8" xl="8" lg="8" md="8" sm="8">
          <div v-if="type.discounted_price !== null && type.discounted_price >= 0"
            class="text-h6 text-decoration-line-through font-weight-bold text-left">
            {{ numberToRupiah(type.producttype_price) }}
          </div>
          <div class="text-h6 font-weight-bold text-left">
            {{ numberToRupiah(type.discounted_price !== null && type.discounted_price >= 0 ? type.discounted_price :
              type.producttype_price) }}
          </div>
        </v-col>
        <v-col class="d-flex justify-end" cols="12" xxl="4" xl="4" lg="4" md="4" sm="4">
          <v-chip v-if="
            validation.quantity.isOutOfStock(type) ||
            validation.quantity.isLessThanMinOrder(type) || (type.is_limited &&
              type.producttype_qty -
              type.producttype_booked_qty <=
              type.qty_threshold_sale)
          " color="error">
            {{ $t("non-event-card.out-of-stock") }}
          </v-chip>
          <v-chip v-else-if="product.is_expired" color="error">
            {{ $t("non-event-card.pullout-end") }}
          </v-chip>
          <v-btn v-else-if="!findProductInCart(type.producttype_id)" variant="flat" :disabled="product.is_upcoming"
            @click="addToCart(type, type.producttype_minimum_order)" color="widget-color"
            class="text-none text-caption">
            {{ $t("non-event-card.choose-ticket") }}
          </v-btn>

          <div class="d-flex align-center" v-else>
            <v-icon size="large" color="widget-color" class="mr-2" @click="removeFromCart(type)">mdi-trash-can</v-icon>
            <v-btn class="mx-2" color="widget-color" size="small" rounded="lg" @click="reduceFromCart(type)"
              icon="mdi-minus" :disabled="!validation.cart.canReduceFromCart(
                type,
                findProductInCart(type.producttype_id)?.qty
              )
                "></v-btn>
            <span v-if="!product.is_rombongan">{{ findProductInCart(type.producttype_id)?.qty }}</span>
            <v-sheet width="75" v-else>
              <v-form ref="requiredInput">
                <v-number-input v-model="findProductInCart(type.producttype_id).qty" hide-details
                  control-variant="hidden" validate-on="blur" @input="validateBlur" @blur="() => {
                    let currentQty = findProductInCart(type.producttype_id).qty;
                    if (type.is_limited && currentQty > type.producttype_qty) {
                      findProductInCart(type.producttype_id).qty = type.producttype_qty ?? type.producttype_minimum_order;
                    } else if (currentQty > type.producttype_maximum_order) {
                      findProductInCart(type.producttype_id).qty = type.producttype_maximum_order;
                    } else if (currentQty < type.producttype_minimum_order) {
                      findProductInCart(type.producttype_id).qty = type.producttype_minimum_order;
                    }
                    validateBlur();
                  }" variant="outlined" color="widget-color" :rules="[
                    v => v >= type.producttype_minimum_order || `Minimal input ${type.producttype_minimum_order}`,
                    v => v <= type.producttype_maximum_order || `Maksimal input ${type.producttype_maximum_order}`,
                  ]"></v-number-input>
              </v-form>
            </v-sheet>
            <v-btn class="mx-2" color="widget-color" size="small" rounded="lg" @click="addToCart(type)" icon="mdi-plus"
              :disabled="!validation.cart.canAddToCart(
                type,
                findProductInCart(type.producttype_id)?.qty
              )
                "></v-btn>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { numberToRupiah } from "@/utils/numberFormatter";
import * as validation from "@/utils/validation";

export default {
  props: {
    type: {
      type: Object,
      require: true,
    },
  },
  computed: {
    product() {
      return this.$store.state.product;
    },
  },

  data() {
    return {
      validation: {
        quantity: validation.default.quantity,
        cart: validation.default.cart,
        input: validation.default.input
      },
    };
  },
  methods: {
    async validateInput() {
      let result = true;
      if (this.product.is_rombongan) {
        const validation = await this.$refs.requiredInput?.validate();
        const valid = validation?.valid !== undefined ? validation.valid : false;
        result = valid
      }

      this.$store.state.requiredInput = result
    },
    validateBlur() {
      setTimeout(() => {
        this.validateInput()
      }, 50);
    },
    getDayInWeek(day) {
      return this.$moment.weekdays(day);
    },
    numberToRupiah(number) {
      return numberToRupiah(number);
    },
    findProductInCart(productTypeID) {
      return this.$store.getters.findProductInCart(productTypeID);
    },
    removeFromCart(productType) {
      this.$store.commit("removeProductFromCart", {
        productType: productType,
      });

      if (this.product.is_rombongan) this.validateInput()
    },
    reduceFromCart(productType) {
      this.$store.commit("reduceProductFromCart", {
        productType: productType,
      });

      if (this.product.is_rombongan) this.validateInput()
    },
    addToCart(productType, qty = 1) {
      if (this.product.is_rombongan) {
        setTimeout(() => {
          this.validateInput()
        }, 50);
      }

      this.$store.commit("addProductToCart", {
        productType: productType,
        qty: qty,
      });

      if (this.product.is_rombongan) this.validateInput()
    },
  },
};
</script>